import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check } from "lucide-react";
import { DisclaimerBlock } from "./DisclaimerBlock";
import { useLang } from "../../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREAS = [
  "ONIX Messenger",
  "ONIX Vision",
  "ONIX Tactical Graph",
  "ONIX Malware Static Analyzer",
  "ONIX AutoSOC/SIEM",
  "Sensorización y ciberdefensa",
  "DWI ONIX",
  "Partnership o integración",
  "Otro",
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

const Field = ({ label, htmlFor, required, error, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-[11px] font-bold uppercase tracking-[0.14em] text-onix-silver mb-2">
      {label} {required && <span aria-hidden="true">*</span>}
    </label>
    {children}
    {error && (
      <p
        role="alert"
        data-testid={`contact-error-${htmlFor.replace("cf-", "")}`}
        className="mt-2 text-xs font-bold text-onix-white flex items-center gap-2"
      >
        <span className="w-1.5 h-1.5 border border-onix-white rotate-45 inline-block" aria-hidden="true" />
        {error}
      </p>
    )}
  </div>
);

export const ContactForm = () => {
  const { t } = useLang();
  const tf = t.form;
  const [form, setForm] = useState({
    nombre: "",
    empresa: "",
    cargo: "",
    email: "",
    telefono: "",
    area: "",
    mensaje: "",
    consentimiento: false,
    website: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.nombre.trim().length < 2) errs.nombre = tf.errNombre;
    if (form.empresa.trim().length < 2) errs.empresa = tf.errEmpresa;
    if (form.cargo.trim().length < 2) errs.cargo = tf.errCargo;
    if (!EMAIL_RE.test(form.email.trim())) errs.email = tf.errEmail;
    if (!form.area) errs.area = tf.errArea;
    if (form.mensaje.trim().length < 10) errs.mensaje = tf.errMensaje;
    if (!form.consentimiento) errs.consentimiento = tf.errConsent;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        telefono: form.telefono.trim() || null,
      });
      setSuccess(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : tf.errServer);
    } finally {
      setSending(false);
    }
  };

  if (success) {
    return (
      <div data-testid="contact-success" className="border border-onix-line p-10 md:p-14">
        <span className="inline-flex items-center justify-center w-12 h-12 border border-onix-silver mb-6">
          <Check size={20} className="text-onix-white" aria-hidden="true" />
        </span>
        <h3 className="font-serif font-bold text-2xl text-onix-white mb-4">{tf.successTitle}</h3>
        <p className="text-sm leading-relaxed text-onix-silver max-w-md">{tf.successText}</p>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} noValidate className="space-y-7">
      <div className="grid md:grid-cols-2 gap-7">
        <Field label={tf.nombre} htmlFor="cf-nombre" required error={errors.nombre}>
          <input data-testid="contact-input-nombre" id="cf-nombre" className="onix-input" value={form.nombre} onChange={set("nombre")} autoComplete="name" maxLength={120} aria-invalid={Boolean(errors.nombre)} />
        </Field>
        <Field label={tf.empresa} htmlFor="cf-empresa" required error={errors.empresa}>
          <input data-testid="contact-input-empresa" id="cf-empresa" className="onix-input" value={form.empresa} onChange={set("empresa")} autoComplete="organization" maxLength={120} aria-invalid={Boolean(errors.empresa)} />
        </Field>
        <Field label={tf.cargo} htmlFor="cf-cargo" required error={errors.cargo}>
          <input data-testid="contact-input-cargo" id="cf-cargo" className="onix-input" value={form.cargo} onChange={set("cargo")} autoComplete="organization-title" maxLength={120} aria-invalid={Boolean(errors.cargo)} />
        </Field>
        <Field label={tf.email} htmlFor="cf-email" required error={errors.email}>
          <input data-testid="contact-input-email" id="cf-email" type="email" className="onix-input" value={form.email} onChange={set("email")} autoComplete="email" maxLength={160} aria-invalid={Boolean(errors.email)} />
        </Field>
        <Field label={tf.telefono} htmlFor="cf-telefono" error={errors.telefono}>
          <input data-testid="contact-input-telefono" id="cf-telefono" type="tel" className="onix-input" value={form.telefono} onChange={set("telefono")} autoComplete="tel" maxLength={40} />
        </Field>
        <Field label={tf.area} htmlFor="cf-area" required error={errors.area}>
          <select data-testid="contact-select-area" id="cf-area" className="onix-input" value={form.area} onChange={set("area")} aria-invalid={Boolean(errors.area)}>
            <option value="">{tf.areaPlaceholder}</option>
            {AREAS.map((a) => (
              <option key={a} value={a}>
                {tf.areaLabels[a] || a}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={tf.mensaje} htmlFor="cf-mensaje" required error={errors.mensaje}>
        <textarea data-testid="contact-input-mensaje" id="cf-mensaje" className="onix-input min-h-[140px] resize-y" value={form.mensaje} onChange={set("mensaje")} maxLength={4000} aria-invalid={Boolean(errors.mensaje)} />
      </Field>

      <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <div className="flex items-start gap-4">
        <input
          data-testid="contact-checkbox-consent"
          id="cf-consent"
          type="checkbox"
          checked={form.consentimiento}
          onChange={(e) => setForm((f) => ({ ...f, consentimiento: e.target.checked }))}
          className="mt-1 w-5 h-5 shrink-0 appearance-none border border-onix-line bg-onix-black2 checked:bg-onix-white cursor-pointer grid place-content-center checked:before:content-['✓'] checked:before:text-onix-black checked:before:text-xs checked:before:font-bold"
          aria-invalid={Boolean(errors.consentimiento)}
        />
        <label htmlFor="cf-consent" className="text-sm leading-relaxed text-onix-silver cursor-pointer">
          {tf.consentPre}{" "}
          <Link to="/privacidad" className="text-onix-white underline underline-offset-4 decoration-onix-line hover:decoration-onix-silver">
            {tf.consentLink}
          </Link>{" "}
          {tf.consentPost}
        </label>
      </div>
      {errors.consentimiento && (
        <p role="alert" data-testid="contact-error-consentimiento" className="text-xs font-bold text-onix-white -mt-4">
          {errors.consentimiento}
        </p>
      )}

      <DisclaimerBlock label={tf.beforeSending}>{tf.disclaimer}</DisclaimerBlock>

      {serverError && (
        <div data-testid="contact-error" role="alert" className="border border-onix-silver border-dashed p-5 text-sm text-onix-silver">
          {serverError}
        </div>
      )}

      <button data-testid="contact-submit" type="submit" disabled={sending} className="btn-onix-primary disabled:opacity-60 disabled:cursor-wait">
        {sending ? tf.sending : tf.submit}
        {!sending && <ArrowRight size={14} aria-hidden="true" />}
      </button>
    </form>
  );
};
